import { some, pick } from 'underscore';
/*
 * Captures:
 * [0]: whole match
 * [1]: data feed name
 * [2]: attribute name
 * [3]: default text(not null)
 */
// for IsAdCustomizerMilestone5Enabled, it enable all characters other than {}
// Looks for a feed name separator
const adCustomizerFeedNameSyntaxWithSpecialCharacter = /{(?:=(?:((?:[^\s{}.\\]|\\[^{}])(?:[^{}.\\]|\\[^{}])*))?}?)?/i;
// Looks for a attribute name starting from the "."
/*
 * Captures:
 * [0]: whole match starting from '.'
 * [1]: attribute name
 * [2]: the '}' character - mark of a complete expression
 */
const adCustomizerFeedAttributeSyntaxWithSpecialCharacter = /^\.((?:[^{}:\\]*|(?:\\[^{}])*)*)(})?/i;
/**
 * Looks for default text starting from the ":".
 * Capture:
 * [0]: whole match starting from ':'
 * [1]: default text
 * [2]: the '}' character - mark of a complete expression
 */
const adCustomizerFeedDefaultTextSyntaxWithSpecialCharacter = /^:(?:(?:\s*(?:((?:[^}\\]|\\\S)*))?)?(?:(}))?)?/i;

function removeEscapedStringFromFeedName(ori) {
  if (ori) {
    return ori.replace(/\\./g, '.');
  }
  return '';
}
function removeEscapedStringFromAttribute(ori) {
  if (ori) {
    return ori.replace(/\\:/g, ':');
  }
  return '';
}

/**
 * Looks for a ad customizer feed definition in given text starting from passed index.
 * If found, it parses the definition.
 * with special character support (character other than {})
 * @param {string} text - text to search in
 * @param {int} startIndex - starts searching at this index
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseAdCustomizerFeedsRuleForSpecialChracter(text, startIndex, cursorIndex) {
  let input = text.substring(startIndex);

  // first find the feedName
  const nameTokens = adCustomizerFeedNameSyntaxWithSpecialCharacter.exec(input);

  // Check if cursor is at the "{" or before that, then exist. Otherwise, continue.
  if (nameTokens === null ||
    (cursorIndex && cursorIndex <= (startIndex + nameTokens.index))) {
    return null;
  }

  const feed = removeEscapedStringFromFeedName(nameTokens[1]);
  // source stores full expression we parsed
  let source = nameTokens[0];
  let attribute = '';
  let defaultText = '';
  const { index } = nameTokens;

  let isComplete = false;
  let specificFeedName = false;
  let specificAttributeName = false;
  // then find the feed attribute

  input = input.substring(index).substring(source.length);

  if (input) {
    const attributeTokens = adCustomizerFeedAttributeSyntaxWithSpecialCharacter.exec(input);

    if (attributeTokens) {
      attribute = attributeTokens.length >= 2 ? removeEscapedStringFromAttribute(attributeTokens[1]) : '';
      isComplete = !!attributeTokens[2];

      if (attributeTokens.length > 0) {
        specificFeedName = true;

        if (attributeTokens[0]) {
          source += attributeTokens[0];
          input = input.substring(attributeTokens[0].length);
        }
      }
    }
  }

  // If there was an attribute part after a feed part, and still some text remains to parse
  if (specificFeedName && input) {
    const defaultTextToken = adCustomizerFeedDefaultTextSyntaxWithSpecialCharacter.exec(input);

    if (defaultTextToken) {
      defaultText = defaultTextToken[1] || '';
      if (defaultTextToken[0]) {
        source += defaultTextToken[0];
      }
      specificAttributeName = true;
      isComplete = isComplete || !!defaultTextToken[2];
    }
  }

  return {
    source,
    isComplete,
    index: nameTokens.index,
    feed,
    attribute,
    defaultText,
    specificFeedName,
    specificAttributeName,
  };
}

export function parseRule({
  processedText,
  startIndex,
  selectionEnd,
  // needFullRule actually means - allow full rule
  needFullRule,
  isSelectedByUser,
}) {
  // the { and we do have ad customizer feeds case
  const parsers = [{
    // Check if cursor is placed in the middle of a valid syntax
    parse() {
      return parseAdCustomizerFeedsRuleForSpecialChracter(
        processedText,
        startIndex, selectionEnd
      );
    },
    keys: ['feed', 'attribute', 'defaultText', 'specificFeedName', 'source', 'specificAttributeName', 'isComplete'],
    inclusive: false,
  }];

  let options = null;
  some(parsers, (parser) => {
    const rule = parser.parse();

    if (!rule) {
      return null;
    }

    let isValid = needFullRule;

    if (!isValid) {
      isValid = isSelectedByUser;
      // for not fullRule and not selected, there should be no '}' in source
      if (!isValid) {
        isValid = rule.source.indexOf('}') < 0;

        if (!isValid) {
          // if all } are the \\}, special characters, then it's fine
          isValid = rule.source.split('}').length === rule.source.split('\\}').length;
        }
      }

      // for the whole rule match, it either did not have } or behind it
      isValid = isValid || (selectionEnd < startIndex + rule.source.length);
    }

    if (isValid) {
      options = pick(rule, parser.keys);

      options.startIndex = startIndex;
      options.endIndex = startIndex + rule.source.length;

      return true;
    }

    return false;
  });

  return options;
}
